import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, limit, onSnapshot, QueryConstraint } from "firebase/firestore";
import { db } from "@/services/firebase/init";
import { convertTimestampToDate } from "@/services/firebase/firestore";
import { FeedbackCase, FeedbackFilters } from "@/types/feedback";
import { usePermissions } from "@/hooks/usePermissions";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminDataTable,
  AdminEmptyState,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { X, Eye } from "lucide-react";

export default function FeedbackListPage() {
  const navigate = useNavigate();
  const { isStaff } = usePermissions();
  const [filters, setFilters] = useState<FeedbackFilters>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [feedbacks, setFeedbacks] = useState<FeedbackCase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 取得待處理數量（使用即時監聽取代輪詢）
  useEffect(() => {
    const q = query(
      collection(db, "feedbacks"),
      where("status", "==", "pending")
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setPendingCount(snapshot.size);
      },
      (err) => {
        console.error("Error getting pending count:", err);
      }
    );
    return () => unsubscribe();
  }, []);

  // 使用 onSnapshot 即時監聽客服單列表
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const constraints: QueryConstraint[] = [];

    if (filters.status) {
      constraints.push(where("status", "==", filters.status));
    }
    if (filters.type) {
      constraints.push(where("type", "==", filters.type));
    }
    if (filters.priority) {
      constraints.push(where("priority", "==", filters.priority));
    }

    constraints.push(orderBy("updatedAt", "desc"));
    constraints.push(limit(100));

    const q = query(collection(db, "feedbacks"), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const feedbacksData = snapshot.docs.map((doc) => {
          const data = convertTimestampToDate(doc.data());
          return {
            ...data,
            caseId: doc.id,
          } as FeedbackCase;
        });
        setFeedbacks(feedbacksData);
        setIsLoading(false);
      },
      (err) => {
        console.error("Error getting feedbacks:", err);
        setError(err as Error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [filters.status, filters.type, filters.priority]);

  // 客戶端搜尋過濾
  const filteredFeedbacks = useMemo(() => {
    if (!searchQuery.trim()) {
      return feedbacks;
    }

    const searchLower = searchQuery.toLowerCase();
    return feedbacks.filter(
      (feedback) =>
        feedback.caseId.toLowerCase().includes(searchLower) ||
        feedback.userId.toLowerCase().includes(searchLower) ||
        feedback.title.toLowerCase().includes(searchLower)
    );
  }, [feedbacks, searchQuery]);

  const handleViewDetail = (caseId: string) => {
    navigate(`/feedbacks/${caseId}`);
  };

  const getStatusLabel = (status: FeedbackCase["status"]): string => {
    switch (status) {
      case "pending": return "待處理";
      case "in_progress": return "處理中";
      case "resolved": return "處理完";
      case "closed": return "已關閉";
      default: return status;
    }
  };

  const getStatusVariant = (status: FeedbackCase["status"]): "default" | "destructive" | "secondary" | "outline" => {
    switch (status) {
      case "pending": return "destructive";
      case "in_progress": return "default";
      case "resolved": return "secondary";
      case "closed": return "outline";
      default: return "outline";
    }
  };

  const getTypeLabel = (type: FeedbackCase["type"]): string => {
    switch (type) {
      case "transaction": return "交易相關";
      case "video": return "影片相關";
      case "account": return "帳戶相關";
      case "suggestion": return "建議";
      case "other": return "其他";
      default: return type;
    }
  };

  const getPriorityVariant = (priority: FeedbackCase["priority"]): "default" | "destructive" | "secondary" | "outline" => {
    switch (priority) {
      case "urgent": return "destructive";
      case "normal": return "default";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const getPriorityLabel = (priority: FeedbackCase["priority"]): string => {
    switch (priority) {
      case "urgent": return "緊急";
      case "normal": return "一般";
      case "low": return "低";
      default: return priority;
    }
  };

  const columns: AdminTableColumn[] = [
    { key: "caseId", label: "客服單編號", hideBelow: "md" },
    { key: "title", label: "標題" },
    { key: "user", label: "用戶", hideBelow: "md" },
    { key: "type", label: "類型", hideBelow: "lg" },
    { key: "status", label: "狀態" },
    { key: "priority", label: "優先級", hideBelow: "md" },
    { key: "updatedAt", label: "更新時間", hideBelow: "lg", sortable: true },
    { key: "actions", label: "操作" },
  ];

  if (!isStaff) {
    return (
      <AdminPage>
        <AdminErrorAlert message="您沒有權限訪問此頁面。只有管理員和工作人員可以處理客服單。" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="客服處理"
        subtitle="管理所有客服單"
        actions={
          pendingCount > 0 ? (
            <Badge variant="destructive" className="text-sm px-3 py-1">
              {pendingCount} 待處理
            </Badge>
          ) : undefined
        }
      />

      {/* 搜尋和篩選 */}
      <Card className="mb-6">
        <CardContent className="p-6 space-y-4">
          {/* 搜尋 */}
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜尋客服單編號、用戶 UID 或標題"
          />

          {/* 篩選器 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* 狀態篩選 */}
            <div className="space-y-2">
              <Label>狀態</Label>
              <Select
                value={filters.status || "all"}
                onValueChange={(val) =>
                  setFilters({
                    ...filters,
                    status: val === "all" ? undefined : (val as FeedbackCase["status"]),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="pending">待處理</SelectItem>
                  <SelectItem value="in_progress">處理中</SelectItem>
                  <SelectItem value="resolved">處理完</SelectItem>
                  <SelectItem value="closed">已關閉</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 類型篩選 */}
            <div className="space-y-2">
              <Label>類型</Label>
              <Select
                value={filters.type || "all"}
                onValueChange={(val) =>
                  setFilters({
                    ...filters,
                    type: val === "all" ? undefined : (val as FeedbackCase["type"]),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="transaction">交易相關</SelectItem>
                  <SelectItem value="video">影片相關</SelectItem>
                  <SelectItem value="account">帳戶相關</SelectItem>
                  <SelectItem value="suggestion">建議</SelectItem>
                  <SelectItem value="other">其他</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 優先級篩選 */}
            <div className="space-y-2">
              <Label>優先級</Label>
              <Select
                value={filters.priority || "all"}
                onValueChange={(val) =>
                  setFilters({
                    ...filters,
                    priority: val === "all" ? undefined : (val as FeedbackCase["priority"]),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="urgent">緊急</SelectItem>
                  <SelectItem value="normal">一般</SelectItem>
                  <SelectItem value="low">低</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 清除篩選 */}
            <div className="flex items-end">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setFilters({});
                  setSearchQuery("");
                }}
              >
                <X className="mr-2 h-4 w-4" />
                清除篩選
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 載入狀態 */}
      {isLoading && <AdminLoadingSkeleton rows={5} />}

      {/* 錯誤狀態 */}
      {error && (
        <AdminErrorAlert
          message={`載入客服單時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      )}

      {/* 客服單列表 */}
      {!isLoading && !error && (
        <>
          {filteredFeedbacks.length > 0 && (
            <p className="text-sm text-muted-foreground mb-3">
              共 {filteredFeedbacks.length} 筆客服單
            </p>
          )}
          <AdminDataTable
            columns={columns}
            data={filteredFeedbacks}
            emptyTitle="沒有找到符合條件的客服單"
            pageSize={20}
            getRowId={(item) => item.caseId}
            onRowClick={(item) => handleViewDetail(item.caseId)}
            renderCell={(feedback, col) => {
              switch (col.key) {
                case "caseId":
                  return (
                    <span className="text-sm font-mono text-muted-foreground">
                      {feedback.caseId}
                    </span>
                  );
                case "title":
                  return (
                    <span className="text-sm font-medium text-foreground max-w-xs truncate block">
                      {feedback.title}
                    </span>
                  );
                case "user":
                  return (
                    <div>
                      <div className="text-sm text-muted-foreground">
                        {feedback.displayName || "未設定名稱"}
                      </div>
                      <div className="text-xs text-muted-foreground/70">
                        {feedback.userId.length > 8
                          ? `${feedback.userId.substring(0, 8)}...`
                          : feedback.userId}
                      </div>
                      {feedback.isAnonymous && (
                        <Badge variant="outline" className="text-xs mt-0.5">匿名</Badge>
                      )}
                    </div>
                  );
                case "type":
                  return (
                    <span className="text-sm text-muted-foreground">
                      {getTypeLabel(feedback.type)}
                    </span>
                  );
                case "status":
                  return (
                    <Badge variant={getStatusVariant(feedback.status)}>
                      {getStatusLabel(feedback.status)}
                    </Badge>
                  );
                case "priority":
                  return (
                    <Badge variant={getPriorityVariant(feedback.priority)}>
                      {getPriorityLabel(feedback.priority)}
                    </Badge>
                  );
                case "updatedAt":
                  return (
                    <span className="text-sm text-muted-foreground">
                      {feedback.updatedAt
                        ? feedback.updatedAt.toLocaleString("zh-TW")
                        : "-"}
                    </span>
                  );
                case "actions":
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetail(feedback.caseId);
                      }}
                    >
                      <Eye className="mr-1 h-3.5 w-3.5" />
                      查看詳情
                    </Button>
                  );
                default:
                  return null;
              }
            }}
          />
        </>
      )}
    </AdminPage>
  );
}
